import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaUserPlus, FaTrash, FaStar, FaSpinner, FaTimes, FaUsers } from 'react-icons/fa';
import { API_URL } from '../config';
import InviteLinkModal from './InviteLinkModal';
import ConfirmDialog from './ConfirmDialog';

/**
 * PHASE 2 — "MY TEAM". Every login that belongs to a client / subcontractor
 * company, with add + remove. Staff can manage any company; a company's own
 * primary contact can manage theirs (the backend enforces both).
 *
 * Props: companyType ('client'|'subcontractor'), companyId, companyName
 */
export default function CompanyUsersCard({ companyType, companyId, companyName, className }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [confirmUser, setConfirmUser] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', role_label: '', projects_only: false });

  const auth = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token') || ''}`
  });

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/company-users/${companyType}/${companyId}`, { headers: auth() });
      const d = await r.json();
      if (d.success) setUsers(d.users || []);
      else toast.error(d.error || 'Could not load the logins');
    } catch (e) {
      toast.error('Could not load the logins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [companyType, companyId]);

  const addUser = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/company-users/${companyType}/${companyId}`, {
        method: 'POST',
        headers: auth(),
        body: JSON.stringify({
          email: form.email.trim(),
          full_name: form.full_name.trim() || null,
          role_label: form.role_label.trim() || null,
          // PHASE 5 — projects-only logins never see quotes/invoices/reports
          portal_scope: form.projects_only ? 'projects_only' : 'full'
        })
      });
      const d = await r.json();
      if (!d.success) { toast.error(d.error || 'Could not add the login'); return; }
      toast.success(d.message || 'Login added');
      setForm({ full_name: '', email: '', role_label: '', projects_only: false });
      setShowAdd(false);
      // Email may not be configured — hand the link over so it can be shared.
      if (d.invite_link && !d.email_sent) {
        setInviteInfo({ company: companyName, email: d.email, link: d.invite_link, emailSent: false });
      }
      load();
    } catch (e) {
      toast.error('Could not add the login');
    } finally {
      setSaving(false);
    }
  };

  const makePrimary = async (u) => {
    setBusy(u.user_id);
    try {
      const r = await fetch(`${API_URL}/company-users/${companyType}/${companyId}/${u.user_id}/primary`, { method: 'POST', headers: auth() });
      const d = await r.json();
      if (d.success) { toast.success(d.message || 'Primary contact updated'); load(); }
      else toast.error(d.error || 'Could not update');
    } catch { toast.error('Could not update'); }
    finally { setBusy(''); }
  };

  // PHASE 5 — flip a login between full portal access and projects-only.
  const setScope = async (u) => {
    const next = u.portal_scope === 'projects_only' ? 'full' : 'projects_only';
    setBusy(u.user_id);
    try {
      const r = await fetch(`${API_URL}/company-users/${companyType}/${companyId}/${u.user_id}/scope`, {
        method: 'POST',
        headers: auth(),
        body: JSON.stringify({ scope: next })
      });
      const d = await r.json();
      if (d.success) { toast.success(d.message || 'Access updated'); load(); }
      else toast.error(d.error || 'Could not update');
    } catch { toast.error('Could not update'); }
    finally { setBusy(''); }
  };

  // Re-send the sign-in invitation for ONE person (e.g. the primary contact never got it).
  // Email may not be configured, so we always hand back the link for copying - it reuses
  // the same modal as "Add login".
  const resendInvite = async (u) => {
    setBusy(u.user_id);
    try {
      const r = await fetch(`${API_URL}/company-users/${companyType}/${companyId}/${u.user_id}/resend-invite`, {
        method: 'POST',
        headers: auth()
      });
      const d = await r.json();
      if (!d.success) { toast.error(d.error || 'Could not resend the invitation'); return; }
      toast.success(d.email_sent ? 'Invitation re-sent' : 'Fresh link created — copy it below');
      if (d.invite_link) {
        setInviteInfo({ company: companyName, email: d.email || u.email, link: d.invite_link, emailSent: !!d.email_sent });
      }
    } catch { toast.error('Could not resend the invitation'); }
    finally { setBusy(''); }
  };

  const removeUser = (u) => setConfirmUser(u);

  const doRemoveUser = async () => {
    const u = confirmUser;
    if (!u) return;
    setBusy(u.user_id);
    try {
      const r = await fetch(`${API_URL}/company-users/${companyType}/${companyId}/${u.user_id}`, { method: 'DELETE', headers: auth() });
      const d = await r.json();
      if (d.success) { toast.success(d.message || 'Login removed'); load(); }
      else toast.error(d.error || 'Could not remove');
    } catch { toast.error('Could not remove'); }
    finally { setBusy(''); }
  };

  return (
    <div className={`bg-white ${className || 'rounded-2xl border p-5'}`} style={{ borderColor: '#E8ECEF' }}>
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-bold text-navy flex items-center gap-2">
          <FaUsers className="text-green" /> Logins &amp; Team {users.length ? `(${users.length})` : ''}
        </h2>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="px-3 py-1.5 bg-green text-white rounded-lg text-xs font-medium hover:bg-navy flex items-center gap-1"
        >
          {showAdd ? <FaTimes size={10} /> : <FaUserPlus size={10} />} {showAdd ? 'Cancel' : 'Add login'}
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Everyone here can sign in and see {companyName || 'this company'}&apos;s data. Give each person their own login — never share one.
      </p>

      {showAdd && (
        <form onSubmit={addUser} className="mb-4 rounded-xl border p-3 space-y-2" style={{ borderColor: '#E8ECEF' }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Full name"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email (they get a sign-in link)"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              value={form.role_label}
              onChange={(e) => setForm({ ...form, role_label: e.target.value })}
              placeholder="Their role (MD, Procurement, Rigger…)"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={form.projects_only}
              onChange={(e) => setForm({ ...form, projects_only: e.target.checked })}
            />
            <strong>Projects only</strong> — they only see the project area (no quotes, invoices or reports). Use this for temporary / external people.
          </label>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-navy text-white rounded-lg text-xs font-medium hover:bg-green hover:text-navy disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add login'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-6 text-center text-gray-400 text-sm"><FaSpinner className="animate-spin inline" /> Loading…</div>
      ) : users.length === 0 ? (
        <p className="text-sm text-gray-400">No logins yet — add the first one above.</p>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.user_id} className="flex items-center justify-between border rounded-xl p-3" style={{ borderColor: '#E8ECEF' }}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-navy truncate flex items-center gap-2 flex-wrap">
                  {u.full_name || u.email}
                  {u.is_primary && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green/10 text-green flex items-center gap-1">
                      <FaStar size={8} /> Primary contact
                    </span>
                  )}
                  {u.role_label && !u.is_primary && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{u.role_label}</span>
                  )}
                  {u.is_active === false && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-500">Inactive</span>
                  )}
                  {u.portal_scope === 'projects_only' && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">Projects only</span>
                  )}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {u.email}
                  {u.phone ? ` · ${u.phone}` : ''}
                  {u.last_login ? ` · last in ${new Date(u.last_login).toLocaleDateString('en-ZA')}` : ' · never signed in'}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  onClick={() => setScope(u)}
                  disabled={busy === u.user_id}
                  title={u.portal_scope === 'projects_only' ? 'Give full portal access' : 'Limit to the project area only (no quotes / invoices / reports)'}
                  className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:border-green hover:text-green disabled:opacity-50"
                >
                  {u.portal_scope === 'projects_only' ? 'Full access' : 'Projects only'}
                </button>
                <button
                  onClick={() => resendInvite(u)}
                  disabled={busy === u.user_id}
                  title="Send a fresh sign-in link (or copy it to share if email is not configured)"
                  className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:border-green hover:text-green disabled:opacity-50"
                >
                  {busy === u.user_id ? 'Sending…' : 'Resend invite'}
                </button>
                {!u.is_primary && (
                  <>
                    <button
                      onClick={() => makePrimary(u)}
                      disabled={busy === u.user_id}
                      title="Make this person the company's primary contact"
                      className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:border-green hover:text-green disabled:opacity-50"
                    >
                      {busy === u.user_id ? <FaSpinner className="animate-spin" size={10} /> : 'Make primary'}
                    </button>
                    <button
                      onClick={() => removeUser(u)}
                      disabled={busy === u.user_id}
                      title="Remove this login from the company"
                      className="text-red-500 hover:text-red-700 disabled:opacity-50"
                    >
                      <FaTrash size={12} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmUser}
        onClose={() => setConfirmUser(null)}
        title="Remove this login?"
        message={`Remove ${confirmUser?.full_name || confirmUser?.email || 'this person'} from ${companyName || 'this company'}? Their account stays - they just lose access to this company's data.`}
        confirmLabel="Remove"
        danger
        onConfirm={doRemoveUser}
      />

      <InviteLinkModal
        open={!!inviteInfo?.link}
        onClose={() => setInviteInfo(null)}
        company={inviteInfo?.company}
        email={inviteInfo?.email}
        link={inviteInfo?.link}
        emailSent={inviteInfo?.emailSent}
      />
    </div>
  );
}
